// API client with error handling for Time Scheduler

import type {
  CreateSessionResponse,
  GetSessionResponse,
  AddSelectionRequest,
  DeleteSelectionRequest,
  APIResponse
} from '../types';

/**
 * Custom error class for API errors
 */
export class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Custom error class for network errors
 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * API Client for Time Scheduler backend
 */
export class APIClient {
  private baseURL: string;

  constructor(baseURL: string = import.meta.env.VITE_API_URL || '/api') {
    this.baseURL = baseURL;
  }

  /**
   * Generic request method with error handling
   * @param endpoint - API endpoint path
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new APIError(response.status, errorText || response.statusText);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      
      // Network or other errors
      if (error instanceof TypeError) {
        throw new NetworkError('Network request failed. Please check your connection.');
      }
      
      throw new NetworkError('An unexpected error occurred.');
    }
  }

  /**
   * Generic POST request
   * @param endpoint - API endpoint path
   * @param data - Optional request body
   * @returns Parsed JSON response
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Creates a new scheduling session
   * @returns Session ID
   */
  async createSession(): Promise<CreateSessionResponse> {
    return this.request<CreateSessionResponse>('/sessions', {
      method: 'POST'
    });
  }

  /**
   * Gets all selections for a session
   * @param sessionId - Unique session identifier
   * @returns Session data with all user selections
   */
  async getSession(sessionId: string): Promise<GetSessionResponse> {
    return this.request<GetSessionResponse>(`/sessions/${sessionId}`, {
      method: 'GET'
    });
  }

  /**
   * Adds a time slot selection for a user
   * @param data - Selection data (sessionId, userName, timeSlot)
   * @returns Success response
   */
  async addSelection(data: AddSelectionRequest): Promise<APIResponse> {
    return this.request<APIResponse>('/selections', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Removes a time slot selection for a user
   * @param data - Selection data (sessionId, userName, timeSlot)
   * @returns Success response
   */
  async deleteSelection(data: DeleteSelectionRequest): Promise<APIResponse> {
    return this.request<APIResponse>('/selections', {
      method: 'DELETE',
      body: JSON.stringify(data)
    });
  }
}

// Export a default instance
export const apiClient = new APIClient();
